import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import ListingCard from '../components/ListingCard';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import IndiaJobsMap from '../components/IndiaJobsMap';
import LogoBanner from '../components/LogoBanner';
import EntryModal from '../components/EntryModal';
import { ArrowRight } from 'lucide-react';

const SectionHeader = ({ title, subtitle, linkTo, linkLabel, light }) => (
  <div className="flex items-end justify-between mb-10">
    <div>
      <h2
        className="text-3xl md:text-4xl font-normal tracking-tight"
        style={{ fontFamily: 'Cormorant Garamond, serif', color: light ? '#FFFFFF' : '#0F2942' }}
      >
        {title}
      </h2>
      {subtitle && <p className="text-sm mt-2" style={{ color: light ? 'rgba(240, 235, 226, 0.6)' : '#0F2942' }}>{subtitle}</p>}
    </div>
    {linkTo && (
      <Link
        to={linkTo}
        className="flex items-center gap-1.5 text-sm font-medium hover:underline whitespace-nowrap"
        style={{ color: light ? '#D4820A' : '#0F2942' }}
        data-testid={`view-all-${title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
      >
        {linkLabel || 'View All'} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </Link>
    )}
  </div>
);

const HomePage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visitorType, setVisitorType] = useState(() => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem('cc_visitor_type');
    } catch (e) {
      return null;
    }
  });
  const [showEntryModal, setShowEntryModal] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return !localStorage.getItem('cc_visitor_type');
    } catch (e) {
      return true;
    }
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchHomepageData = useCallback(async () => {
    try {
      const { data: result } = await axios.get(`${API_URL}/api/listings/homepage`);
      setData(result);
    } catch (error) {
      console.error('Error fetching homepage data:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchHomepageData();
  }, [fetchHomepageData]);

  const handleVisitorSelect = (type) => {
    setVisitorType(type);
    setShowEntryModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
        <Navbar />
        <div className="px-6 md:px-12 lg:px-24 py-32 text-center">
          <p style={{ color: '#0F2942' }}>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  const listings = data?.listings || [];
  const isFallback = !!data?.is_fallback;
  const sectionTitle = isFallback ? 'Selected Opportunities' : 'Featured Opportunities';
  const sectionSubtitle = isFallback
    ? 'A curated look at the latest opportunities on the platform'
    : 'Highlighted roles from organisations on the platform';

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
      {showEntryModal && <EntryModal onSelect={handleVisitorSelect} />}
      <Navbar />

      <SEO
        title="Arts & Culture Jobs in India | Gigs, Creative Work & Cultural Careers"
        path="/"
        description="Find arts jobs, creative gigs, gallery work, museum roles, festival staffing and part-time cultural opportunities across India. Browse free or hire through India's only specialist cultural recruiter."
      />
      <section className="px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-20 md:pb-28" style={{ backgroundColor: '#F0EBE2' }} data-testid={`hero-${visitorType || 'talent'}`}>
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 items-center max-w-7xl">
          <div>
          {visitorType === 'employer' ? (
            <>
              <span
                className="inline-block text-xs uppercase tracking-[0.25em] font-semibold mb-7"
                style={{ color: '#D4820A' }}
                data-testid="hero-tag-pill"
              >
                Specialist recruitment for the cultural sector
              </span>
              <h1
                className="font-normal tracking-tight mb-8"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: '#0F2942',
                  fontSize: 'clamp(2.25rem, 4.6vw, 3.75rem)',
                  lineHeight: 1.08,
                  letterSpacing: '-0.015em'
                }}
                data-testid="hero-headline"
              >
                Finding creative talent is a problem. We solve it.
              </h1>
              <div className="w-14 h-px mb-7" style={{ backgroundColor: '#D4820A' }} />
              <p className="text-base md:text-lg leading-relaxed font-light max-w-xl mb-10" style={{ color: '#0F2942' }}>
                Permanent roles, freelance, and festival crew across India&rsquo;s arts and cultural sector. We source, screen and shortlist the right person for the role.
              </p>

              <div className="flex flex-wrap gap-3 mb-6">
                <Link
                  to="/hire"
                  className="inline-flex items-center px-6 py-3.5 text-sm font-medium transition-all hover:opacity-90"
                  style={{ backgroundColor: '#D4820A', color: '#0F2942', borderRadius: '999px' }}
                  data-testid="hero-primary-cta"
                >
                  Hire through us <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Link>
                <Link
                  to="/post-job"
                  className="inline-flex items-center px-6 py-3.5 text-sm font-medium transition-all hover:bg-black/5"
                  style={{ border: '1.5px solid #0F2942', color: '#0F2942', borderRadius: '999px' }}
                  data-testid="hero-secondary-cta"
                >
                  Post a Job
                </Link>
              </div>

              <p className="text-sm" style={{ color: '#0F2942' }}>
                Looking for work instead?{' '}
                <button
                  type="button"
                  onClick={() => setShowEntryModal(true)}
                  className="underline hover:text-[#0F2942] transition-colors"
                  style={{ color: '#0F2942' }}
                  data-testid="hero-switch-view"
                >
                  Switch to talent view
                </button>
              </p>
            </>
          ) : (
            <>
              <span
                className="inline-block text-xs uppercase tracking-[0.25em] font-semibold mb-7"
                style={{ color: '#D4820A' }}
                data-testid="hero-tag-pill"
              >
                India&rsquo;s cultural sector hiring platform
              </span>
              <h1
                className="font-normal tracking-tight mb-8"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: '#0F2942',
                  fontSize: 'clamp(2.25rem, 4.6vw, 3.75rem)',
                  lineHeight: 1.08,
                  letterSpacing: '-0.015em'
                }}
                data-testid="hero-headline"
              >
                The career you want in the industry you love.
              </h1>
              <div className="w-14 h-px mb-7" style={{ backgroundColor: '#D4820A' }} />
              <p className="text-base md:text-lg leading-relaxed font-light max-w-xl mb-10" style={{ color: '#0F2942' }}>
                Browse roles across India&rsquo;s arts and culture sector. Find full-time positions to festival gigs. Or join our Talent Network and let the right organisations find you.
              </p>

              <div className="flex flex-wrap gap-3 mb-6">
                <Link
                  to="/browse"
                  className="inline-flex items-center px-6 py-3.5 text-sm font-medium transition-all hover:opacity-90"
                  style={{ backgroundColor: '#D4820A', color: '#0F2942', borderRadius: '999px' }}
                  data-testid="hero-primary-cta"
                >
                  Browse opportunities <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                </Link>
                <Link
                  to="/talent"
                  className="inline-flex items-center px-6 py-3.5 text-sm font-medium transition-all hover:bg-black/5"
                  style={{ border: '1.5px solid #0F2942', color: '#0F2942', borderRadius: '999px' }}
                  data-testid="hero-secondary-cta"
                >
                  Join Talent Network
                </Link>
              </div>

              <p className="text-sm" style={{ color: '#0F2942' }}>
                Looking to hire instead?{' '}
                <button
                  type="button"
                  onClick={() => setShowEntryModal(true)}
                  className="underline hover:text-[#0F2942] transition-colors"
                  style={{ color: '#0F2942' }}
                  data-testid="hero-switch-view"
                >
                  Switch to employer view
                </button>
              </p>
            </>
          )}
          </div>

          {/* Cinematic sizzle reel — autoplay muted loop, desktop forward */}
          <div className="block" data-testid="hero-video" aria-hidden="true">
            <div
              className="relative overflow-hidden mx-auto lg:ml-auto lg:mr-0 w-full max-w-[460px]"
              style={{ aspectRatio: '9/16', backgroundColor: '#0F2942', borderRadius: '12px' }}
            >
              <video
                src="/media/sizzle-hero.mp4"
                poster="/media/poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ aspectRatio: '9/16' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Logo Banner */}
      <section className="px-6 md:px-12 lg:px-24 py-10 md:py-12" style={{ backgroundColor: '#F0EBE2' }} data-testid="logo-banner-section">
        <LogoBanner />
      </section>

      {/* === EMPLOYER-VIEW ONLY HOMEPAGE === */}
      {visitorType === 'employer' ? (
        <>
          {/* 2. Problem recognition cards */}
          <section className="px-6 md:px-12 lg:px-24 py-20 md:py-24" style={{ backgroundColor: '#0F2942' }} data-testid="emp-problem-cards">
            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
              {[
                { h: "You posted on Instagram.", b: "You got 200 responses. None of them were right for the role." },
                { h: "Your last hire didn\u2019t work out.", b: "The brief was fine. The search wasn\u2019t specialist enough." },
                { h: "You need someone by the 15th.", b: "You don\u2019t have the time or the network to find the right person fast." }
              ].map((c, i) => (
                <div key={i} data-testid={`emp-problem-${i + 1}`}>
                  <h3
                    className="text-2xl md:text-3xl font-bold leading-tight mb-4 text-white"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {c.h}
                  </h3>
                  <p className="text-base md:text-lg leading-relaxed" style={{ color: 'rgba(240, 235, 226, 0.72)' }}>
                    {c.b}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* 2.5 Bold market-context band — between problem cards and how we work */}
          <section
            className="px-6 md:px-12 lg:px-24 py-20 md:py-28"
            style={{ backgroundColor: '#F0EBE2' }}
            data-testid="emp-market-context-band"
          >
            <div className="max-w-5xl mx-auto">
              <p
                className="font-bold tracking-tight"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: '#0F2942',
                  fontSize: 'clamp(1.75rem, 4vw, 3rem)',
                  lineHeight: 1.18,
                  letterSpacing: '-0.01em'
                }}
              >
                India has over <span style={{ color: '#D4820A' }}>800 registered museums</span>,{' '}
                <span style={{ color: '#D4820A' }}>2,500 art galleries</span>, and hundreds of cultural festivals.
                Almost none have a dedicated recruiter.{' '}
                <span style={{ fontStyle: 'italic' }}>We&rsquo;re here to change that.</span>
              </p>
            </div>
          </section>

          {/* 3. How we work */}
          <section className="px-6 md:px-12 lg:px-24 py-20 md:py-24" data-testid="emp-how-it-works">
            <div className="max-w-6xl mx-auto">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-14"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
              >
                How we work
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-12">
                {[
                  { t: 'Brief us', b: "You tell us about the role, the team, and what good looks like. We ask the questions a generalist recruiter wouldn\u2019t think to ask." },
                  { t: 'We search', b: 'We draw from our active talent network and reach out directly. You receive three to five vetted, relevant candidates \u2014 not forty CVs.' },
                  { t: 'You hire', b: 'You meet the shortlist. You make the call. We handle offer coordination if needed. You pay only on successful placement.' }
                ].map((s, i) => (
                  <div key={s.t}>
                    <p
                      className="text-sm font-semibold mb-4"
                      style={{ color: '#D4820A', fontFamily: "'Playfair Display', serif", letterSpacing: '0.02em' }}
                    >
                      0{i + 1}
                    </p>
                    <h3
                      className="text-2xl md:text-[28px] font-normal tracking-tight mb-4"
                      style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
                    >
                      {s.t}
                    </h3>
                    <p className="text-base leading-relaxed" style={{ color: '#0F2942' }}>
                      {s.b}
                    </p>
                  </div>
                ))}
              </div>

              <div className="text-center mt-14">
                <Link
                  to="/hire"
                  className="inline-flex items-center text-sm font-medium hover:underline"
                  style={{ color: '#0F2942' }}
                  data-testid="emp-see-full-details"
                >
                  See full details about hiring through us <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </section>

          {/* 4. What we place */}
          <section className="px-6 md:px-12 lg:px-24 py-20 md:py-24" style={{ backgroundColor: '#F0EBE2' }} data-testid="emp-what-we-place">
            <div className="max-w-5xl">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
              >
                Every role. Full-time, freelance, or a six-hour gig.
              </h2>
              <p className="text-base md:text-lg leading-relaxed max-w-3xl mb-10" style={{ color: '#0F2942' }}>
                Whether you need a curator, a communications manager, a muralist from a specific community, or someone to work a music festival this weekend. If it&rsquo;s a creative or cultural role, we can find the right person.
              </p>
              <div className="flex flex-wrap gap-2.5">
                {[
                  'Gig & Festival Staffing', 'Freelance & Contract', 'Curation & Collections',
                  'Gallery & Museum Management', 'Marketing & Communications', 'Events & Production',
                  'Design & Creative Direction', 'Education & Outreach', 'Digital & Content',
                  'Fundraising & Development', 'Operations & Administration', 'Visitor Experience & Front of House'
                ].map((t) => (
                  <span
                    key={t}
                    className="text-sm rounded-full px-4 py-2"
                    style={{ border: '1px solid rgba(212,130,10,0.5)', color: '#D4820A', backgroundColor: 'transparent' }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* 5. Enquiry CTA band */}
          <section className="px-6 md:px-12 lg:px-24 py-20 md:py-24" style={{ backgroundColor: '#0F2942' }} data-testid="emp-enquiry-cta">
            <div className="max-w-3xl mx-auto text-center">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-5 text-white"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Tell us who you need.
              </h2>
              <p className="text-base md:text-lg font-light leading-relaxed mb-10" style={{ color: 'rgba(240, 235, 226, 0.72)' }}>
                First conversation is always free. We respond within 2 working days.
              </p>
              <Link
                to="/hire#hire-form"
                className="inline-flex items-center px-8 py-3.5 text-sm font-medium rounded-lg transition-all hover:opacity-90"
                style={{ backgroundColor: '#D4820A', color: '#0F2942' }}
                data-testid="emp-start-conversation"
              >
                Start a conversation <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Link>
            </div>
          </section>

          {/* 6. Secondary option footnote */}
          <section className="px-6 md:px-12 lg:px-24 py-8 md:py-10" style={{ backgroundColor: '#F0EBE2' }} data-testid="emp-footnote-band">
            <p className="text-sm text-center" style={{ color: '#0F2942' }}>
              Not ready for a full search? Post a role directly and reach our candidate network.{' '}
              <Link to="/post-job" className="underline hover:text-[#0F2942]" style={{ color: '#0F2942' }}>
                Post a role
              </Link>
            </p>
          </section>
        </>
      ) : (
        <>
      {listings.length > 0 && (
        <section
          className="px-6 md:px-12 lg:px-24 py-14 md:py-20"
          style={{ backgroundColor: '#F0EBE2' }}
          data-testid="featured-opportunities-section"
        >
          <SectionHeader
            title={sectionTitle}
            subtitle={sectionSubtitle}
            linkTo="/browse"
            linkLabel="Browse All"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.slice(0, 6).map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                variant="featured"
                showFeaturedBadge={!isFallback}
              />
            ))}
          </div>

          {/* Inline talent network CTA strip */}
          <div
            className="mt-12 rounded-xl px-6 md:px-10 py-6 md:py-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(212,130,10,0.35)' }}
            data-testid="talent-network-cta"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-1" style={{ color: '#D4820A' }}>
                For Creatives
              </p>
              <h3
                className="text-2xl md:text-3xl font-normal tracking-tight"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
              >
                Join a growing community of 2,000+ creative professionals across India
              </h3>
              <p className="text-sm mt-1" style={{ color: '#0F2942' }}>
                Submit your profile and resume so cultural organisations discover you first.
              </p>
            </div>
            <Link
              to="/talent"
              className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-all hover:opacity-90 whitespace-nowrap"
              style={{ backgroundColor: '#D4820A', color: '#0F2942' }}
              data-testid="talent-network-cta-button"
            >
              Submit your profile <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Link>
          </div>
        </section>
      )}

      {/* Editorial sizzle moment — between Featured and Map */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24" style={{ backgroundColor: '#F0EBE2' }} data-testid="photo-strip">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color: '#D4820A' }}>
              In the field
            </p>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
            >
              Where the work is happening.
            </h2>
            <p className="text-base md:text-lg leading-relaxed font-light mb-6" style={{ color: '#0F2942' }}>
              From gallery jobs in Delhi and museum roles in Mumbai to cultural jobs across Bangalore, Kolkata, Jaipur and Chennai. We cover full-time creative roles, gig work for artists in India, temporary creative work, and remote creative jobs in India.
            </p>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: '#0F2942', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}>
              The work is being made every day. Festivals, openings, residencies, classrooms. Our job is to connect the people doing it.
            </p>
          </div>
          <div className="flex justify-center md:justify-end">
            <div
              className="relative overflow-hidden rounded-xl shadow-xl w-full max-w-[360px]"
              style={{ aspectRatio: '9/16', backgroundColor: '#0F2942' }}
              data-testid="field-video"
              aria-hidden="true"
            >
              <video
                src="/media/sizzle-hero.mp4"
                poster="/media/poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ aspectRatio: '9/16' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Jobs Map */}
      <section className="px-6 md:px-12 lg:px-24 py-10 md:py-12" style={{ backgroundColor: '#0F2942' }} data-testid="map-section">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
          <div className="lg:col-span-2">
            <h2
              className="text-3xl md:text-4xl font-normal tracking-tight mb-4 text-white"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Opportunities Across India
            </h2>
            <div className="w-12 h-px mb-6" style={{ backgroundColor: 'rgba(212,130,10,0.5)' }} />
            <p className="text-base leading-relaxed mb-8" style={{ color: 'rgba(240, 235, 226, 0.6)' }}>
              Creative opportunities are spread across the country. Click any city to explore what's available near you.
            </p>
            <Link
              to="/browse"
              className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-all hover:opacity-90"
              style={{ backgroundColor: '#D4820A', color: '#0F2942' }}
            >
              Browse All Cities <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="lg:col-span-3">
            <IndiaJobsMap />
          </div>
        </div>
      </section>

      {/* Dual Path CTA — Employers + Creatives */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20" style={{ backgroundColor: '#0F2942' }} data-testid="dual-cta-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-normal tracking-tight mb-4 text-white"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Join India's creative community
            </h2>
            <div className="w-16 h-px mx-auto" style={{ backgroundColor: 'rgba(212,130,10,0.5)' }} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Employers card */}
            <div
              className="rounded-xl p-8 md:p-10 border flex flex-col"
              style={{ backgroundColor: 'rgba(240, 235, 226, 0.03)', borderColor: 'rgba(212,130,10,0.25)' }}
            >
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-3" style={{ color: '#D4820A' }}>
                For Employers
              </p>
              <h3
                className="text-2xl md:text-3xl font-normal tracking-tight mb-3 text-white"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Have an opportunity to share?
              </h3>
              <p className="text-sm leading-relaxed mb-8 flex-1" style={{ color: 'rgba(240, 235, 226, 0.65)' }}>
                Reach thousands of artists, curators, designers and cultural professionals across India. Post jobs, gigs, residencies, open calls, or speaking opportunities.
              </p>
              <Link
                to="/post-job"
                className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium rounded-lg transition-all hover:opacity-90 w-full md:w-auto self-start"
                style={{ backgroundColor: '#D4820A', color: '#0F2942' }}
                data-testid="cta-post-button"
              >
                Post a Job <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Link>
            </div>

            {/* Creatives card */}
            <div
              className="rounded-xl p-8 md:p-10 border flex flex-col"
              style={{ backgroundColor: 'rgba(240, 235, 226, 0.03)', borderColor: 'rgba(212,130,10,0.25)' }}
            >
              <p className="text-xs uppercase tracking-[0.2em] font-medium mb-3" style={{ color: '#D4820A' }}>
                For Creatives
              </p>
              <h3
                className="text-2xl md:text-3xl font-normal tracking-tight mb-3 text-white"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Looking for your next creative role?
              </h3>
              <p className="text-sm leading-relaxed mb-8 flex-1" style={{ color: 'rgba(240, 235, 226, 0.65)' }}>
                Join our Talent Network so organisations discover you first. Share your portfolio, career stage, and what you're open to. We'll surface you to the right people.
              </p>
              <Link
                to="/talent"
                className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium rounded-lg transition-all hover:bg-white/10 w-full md:w-auto self-start border"
                style={{ borderColor: 'rgba(212,130,10,0.6)', color: '#FFFFFF' }}
                data-testid="cta-talent-button"
              >
                Join Our Talent Network <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>
        </>
      )}

      <Footer />
    </div>
  );
};

export default HomePage;
